/**
 * Hook to lazy load theme enrichment data (colors, hierarchy)
 * Only fetches themes that are actually used by events
 * Used to enrich event theme badges and filters without blocking initial render
 */

'use client';

import { useEffect, useState } from 'react';
import type { Theme } from '@uicwebmaster/taxonomy-themes';

/**
 * Minimal theme data from GraphQL
 */
export interface MinimalTheme {
  id: string;
  name: string;
}

/**
 * Hook to lazy load complete theme data for enrichment
 * Fetches themes from Drupal to get colors and hierarchy
 * 
 * @param themeIds - Array of theme IDs to fetch
 * @param enabled - Whether to fetch (default: true)
 * @returns Object with themes array, loading state, and error
 * 
 * @example
 * ```tsx
 * function EventBadges({ event }) {
 *   const themeIds = event.theme?.map(t => t.id) || [];
 *   const { themes, loading } = useThemeEnrichment(themeIds);
 *   
 *   if (loading) return <Skeleton />;
 *   
 *   return themes.map(theme => (
 *     <ThemeBadge key={theme.id} theme={theme} />
 *   ));
 * }
 * ```
 */
export function useThemeEnrichment(
  themeIds: string[],
  enabled: boolean = true
): {
  themes: Theme[];
  loading: boolean;
  error: Error | null;
} {
  const [themes, setThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || themeIds.length === 0) {
      return;
    }

    let cancelled = false;

    async function fetchThemes() {
      setLoading(true);
      setError(null);

      try {
        // Import dynamically to avoid bundling server-side code
        const { getThemesByIds } = await import('@uicwebmaster/taxonomy-themes/client');

        const fetchedThemes = await getThemesByIds(themeIds);

        if (!cancelled) {
          setThemes(fetchedThemes);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch themes'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchThemes();

    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(themeIds.sort()), enabled]);

  return { themes, loading, error };
}

/**
 * Hook to get theme color by ID
 * Uses cached themes from useThemeEnrichment
 * 
 * @param themeId - Theme ID
 * @param enrichedThemes - Enriched themes from useThemeEnrichment
 * @returns Theme color or null
 */
export function useThemeColor(themeId: string, enrichedThemes: Theme[]): string | null {
  const theme = enrichedThemes.find(t => t.id === themeId);
  return theme?.color || null;
}

